<template>
    <div>
        <div class="mt-6 pb-6" v-if="enrollment.course.applications.length === 0">
            <ui-card class="pt-0 pl-4 pr-6">
                <div class="columns is-gapless mb-6 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">This course does not have any applications or forms.</div>
                    </div>
                </div>
            </ui-card>
        </div>
        <ui-card v-else class="pt-4 pl-4 pr-6 pb-3 mb-5" v-for="app in enrollment.course.applications">
            <div class="columns is-gapless">
                <div class="column is-narrow is-size-6">
                    <i class="fad fa-fw fa-2x has-text-primary mr-5" :class="{'fa-file-certificate': app.applications.type === 'application', 'fa-file-invoice': app.applications.type === 'form'}"></i>
                </div>
                <div class="column mr-5">
                    <div class="has-text-weight-bold is-size-5 mt-1">
                        {{ app.applications.title }}
                        <b-tooltip label="Additional Fee Required" type="is-info" v-if="chargeAmount(app) > 0">
                            <b-tag type="is-warning is-light" class="ml-3 mr-3"><i class="fas fa-dollar"></i></b-tag>
                        </b-tooltip>
                        <!-- Required badge is a readiness-gated nag: only shown once the
                             student is genuinely done working (never earlier) -->
                        <b-tooltip label="Required Application" type="is-info" v-if="app.is_required_at_end && !getEnrollApp(app).completed_at && applicationReadiness">
                            <b-tag type="is-warning is-light" class="ml-3 mr-3"><i class="fas fa-exclamation-triangle mr-2"></i>Required</b-tag>
                        </b-tooltip>

                    </div>
                    <div class="is-size-6 has-text-grey" v-if="app.applications.type === 'application'">{{ app.applications.certification_certs.certifications.title }}</div>
                    <div class="is-size-6 has-text-grey mt-3" v-html="app.applications.description"></div>

                    <div class="mt-3" v-if="app.is_required_at_start && app.isMissingRequired">
                        <div><i class="fas fa-exclamation-triangle has-text-warning mr-2 is-size-6"></i>Completion of this {{ app.applications.type }} is required before starting the course.</div>
                    </div>

                    <div v-if="!app.is_enabled && !getEnrollApp(app).completed_at" class="mt-5">
                        <b-message type="is-warning">
                            <div>This application is not available at this time. Either it has not been made available yet, or it has expired. For the most accurate and up-to-date information, please contact the course's provider.</div>
                            <div class="mt-4 pr-6 has-text-weight-bold">
                                <i class="far fa-fw fa-arrow-right mr-2"></i>Please, check back later or contact the course's provider for more information.
                            </div>
                        </b-message>
                    </div>

                    <section v-else>
                        <div v-if="!getEnrollApp(app).id">
                            <div v-if="app.applications.certification_certs && app.applications.certification_certs.certifications.type === 'unit'">
                                <div class="mt-5 pr-6 pb-2">You have recorded <strong>{{ calcUnits(app).toFixed(2) }}</strong> of <strong>{{ availableUnits(app).toFixed(2) }}</strong> available {{ app.applications.certification_certs.certifications.unit_title }}.</div>

                                <div v-if="calcUnits(app) > 0">
                                    <b-progress
                                        size="is-small"
                                        type="is-success"
                                        :value="calcUnits(app)"
                                        :max="availableUnits(app)"
                                        class="mb-4"
                                    ></b-progress>
                                    <div class="help"><i class="far fa-info-circle mr-1"></i> {{ app.applications.certification_certs.certifications.unit_title }} are calculated from your logged study time, up to the hours you enrolled in. Note that the final {{ app.applications.certification_certs.certifications.unit_title }} calculation may differ due to rounding.</div>
                                </div>
                                <div class="mt-3" v-else>
                                    <i class="far fa-fw fa-arrow-right mr-2"></i>Your log is empty. Add sessions from the <strong><i class="far fa-calendar-circle-user mr-2 ml-1"></i>Sessions & Schedules</strong> or watch <strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong> to contribute to the log.
                                </div>
                            </div>

                            <hr/>

                            <div v-if="!app.ready.toggle">
                                <b-button type="is-info" v-if="app.applications.type === 'application'" @click="start(app)"><strong>Start Application</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                                <b-button type="is-info" v-if="app.applications.type === 'form'" @click="apply(app)"><strong>Start Form</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                            </div>

                            <div v-show="app.ready.toggle">
                                <div class="mt-6">
                                    <b-message type="is-info">
                                        <p class="has-text-weight-bold is-size-5">Are you ready to apply?</p>
                                        <p class="mb-5">Please review and check the following to verify that you are ready to apply for this certification:</p>

                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.log"></b-switch>
                                            </div>
                                            <div class="column">
                                                I have double-checked my <strong><i class="far fa-clipboard-list-check mr-2 ml-1"></i>Study Log</strong> for accuracy and have verified that all entries are truthful and accurate, if applicable.
                                            </div>
                                        </div>
                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.confirm"></b-switch>
                                            </div>
                                            <div class="column">
                                                I understand that once I apply, <u>changes to my log will not make any changes to this application or this certification</u>. You cannot reapply for this certification once you have applied.
                                            </div>
                                        </div>
                                        <section v-if="chargeAmount(app) > 0">
                                            <hr style="background: rgb(158 198 231)"/>
                                            <div class="columns is-gapless">

                                                <div class="column is-narrow">
                                                    <b-switch type="is-success" class="mr-4" v-model="app.ready.charge"></b-switch>
                                                </div>
                                                <div class="column">
                                                    <p v-if="app.applications.certification_certs && app.applications.certification_certs.certifications.type === 'unit'">I understand the cost of this application is <strong>{{ chargeAmount(app) | currency }}</strong> for <strong>{{ calcUnits(app).toFixed(2) }} {{ app.applications.certification_certs.certifications.unit_title }}</strong>.  This fee is separate from any registration fees, if applicable, and will be collected at the end of the application, if approved.</p>
                                                    <p v-else>I understand the cost of this application is <strong>{{ chargeAmount(app) | currency }}</strong> for <strong>{{ app.applications.certification_certs.certifications.unit_title }}</strong>.  This fee is separate from enrollment fees, if applicable, and will be collected at the end of the application, if approved.</p>
                                                </div>
                                            </div>
                                        </section>
                                    </b-message>

                                    <b-message class="mt-5">
                                        <section>
                                            <p class="has-text-weight-bold is-size-5 ">Other Requirements</p>

                                            <div class="mb-3">
                                                <div class="columns is-gapless" v-if="!enrollment.isCourseworkComplete">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must finish all lessons in this course to apply for this certification. Please review the course content in the <strong><i class="far fa-rectangle-list mr-2 ml-1"></i>Course Outline</strong>.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must finish all lessons in this course to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="mb-3" v-if="milestoneLock.locked">
                                                <div class="columns is-gapless">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">A milestone reflection is awaiting approval. It must be approved before you can apply. Review your <strong><i class="far fa-flag-swallowtail mr-2 ml-1"></i>Milestones &amp; Reflections</strong>.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="app.min_minutes_required" class="mb-3">
                                                <div class="columns is-gapless" v-if="totalLogMinutes < app.min_minutes_required">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have recorded at least <strong>{{ app.min_minutes_required }}</strong> minute(s) to apply for this certification. Please review your <strong><i class="far fa-clipboard-list-check mr-2 ml-1"></i>Study Log</strong>.</div>
                                                        <div class="mt-1 is-size-7 has-text-grey">You currently have {{ totalLogMinutes }} minute(s) recorded.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have recorded at least <strong>{{ app.min_minutes_required }}</strong> minute(s) to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </b-message>
                                </div>

                                <div class="mt-6" v-if="app.applications.type === 'application'">
                                    <b-button
                                        size="is-large"
                                        type="is-info"
                                        expanded
                                        :disabled="!applyReady(app) || processing"
                                        @click="apply(app)"
                                        v-if="app.applications.certification_certs && app.applications.certification_certs.certifications.type === 'unit'"
                                    >
                                        <i class="fas fa-arrow-right mr-4"></i>Apply for <strong>{{ calcUnits(app).toFixed(2) }} {{ app.applications.certification_certs.certifications.unit_title }}</strong>
                                    </b-button>

                                    <b-button
                                        size="is-large"
                                        type="is-info"
                                        expanded
                                        :disabled="!applyReady(app) || processing"
                                        @click="apply(app)"
                                        v-else
                                    >
                                        <i class="fas fa-arrow-right mr-4"></i>Apply for <strong> {{ app.applications.certification_certs.certifications.unit_title }}</strong>
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="!getEnrollApp(app).completed_at">
                            <div class="is-size-6 mt-4 has-text-weight-bold"><i class="fas fa-exclamation-triangle has-text-warning mr-2"></i>Application Incomplete. You are on step {{ getEnrollApp(app).progress }} of {{ app.applications.application_outline.length + ((getEnrollApp(app).quoted_price) ? 2 : 1) }}.</div>
                            <div class="is-size-6 mt-4 mb-2 has-text-grey" v-if="getEnrollApp(app).review_at"><i class="far fa-fw fa-info-circle mr-2 has-text-warning"></i>You have reached a step that requires further review and approval. Please allow up to 48 hours for this step to be completed.</div>
                            <hr/>
                            <b-button :type="(getEnrollApp(app).review_at) ? 'is-light' : 'is-danger'" tag="router-link" :to="{name: 'course-application', params: {applicationId: getEnrollApp(app).id}}"><strong>Continue {{ app.applications.type | capitalize }}</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                        </div>

                        <div v-else>
                            <div class="is-size-6 mt-2 has-text-grey"><i class="far fa-fw fa-check-circle mr-2 has-text-success"></i>{{ app.applications.type | capitalize }} completed and approved.</div>
                            <hr/>
                            <b-button type="is-success" tag="router-link" :to="{name: 'course-application', params: {applicationId: getEnrollApp(app).id}}"><strong>View {{ app.applications.type | capitalize }}</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                        </div>
                    </section>

                </div>
            </div>
        </ui-card>
    </div>
</template>

<script>
    import {client as http} from "../../../http_client";
    import UiCard from "../../../components/UiCard.vue";
    import async from "async";
    import {mapActions, mapGetters} from "vuex";

    export default {
        components: {UiCard},
        props: ['enrollmentId'],
        data: function() {
            return {
                processing: false,
            };
        },
        computed: {
            ...mapGetters(['enrollment', 'milestoneLock', 'applicationReadiness']),
            totalLogMinutes: function() {
                let total = 0;
                for (let i = 0; i < this.enrollment.study_log.length; i++) {
                    total += this.enrollment.study_log[i].mins;
                }
                return total;
            }
        },
        methods: {
            ...mapActions(['getEnrollmentById']),
            start(app) {
                app.ready.toggle = true;
            },
            apply(app) {
                if (!this.processing) {
                    this.processing = true;
                    http.post('app/enrollments/' + this.enrollmentId + '/applications', {course_application_id: app.id}).then(response => {
                        this.$router.push({name: 'course-application', params: {applicationId: response.data.id}});
                    });
                }
            },
            getEnrollApp: function(course_app) {
                for (let i = 0; i < this.enrollment.applications.length; i++) {
                    const app = this.enrollment.applications[i];
                    if (app.course_application_id === course_app.id) {
                        return app;
                    }
                }
                return {};
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
            availableUnits(app) {
                // What THIS enrollment can be awarded: the enrolled tier caps
                // the course-wide max_units (a 15-hour enrollment on a course
                // whose biggest tier is 50 offers 15, not 50 — matches the
                // server-side cap in apply())
                const max = parseFloat(app.max_units);
                const cap = parseFloat(this.enrollment.configs?.[0]?.max_hours);
                return (cap > 0) ? Math.min(max, cap) : max;
            },
            calcUnits(app) {
                let certification = app.applications.certification_certs.certifications;
                if (certification.type !== 'unit') {
                    return 0;
                }
                let units = this.totalLogMinutes / certification.unit_ratio;
                if (certification.unit_round_to) {
                    units = Math.floor(this.totalLogMinutes / certification.unit_round_to) * certification.unit_round_to;
                }
                return Math.min(this.availableUnits(app), units);
            },
            chargeAmount(app) {
                let flatPrice = parseFloat(app.flat_price);
                let pricePerUnit = parseFloat(app.per_unit_price);
                if (flatPrice) {
                    return flatPrice;
                } else if (pricePerUnit) {
                    return this.calcUnits(app) * pricePerUnit;
                } else {
                    return 0;
                }
            },
            applyReady(app) {
                if (app.min_minutes_required) {
                    app.ready.minMinutes = this.totalLogMinutes >= app.min_minutes_required;
                } else {
                    app.ready.minMinutes = true;
                }

                // Gate inversion (completion redesign): applying requires finished
                // coursework — not enrollment.completed_at, which the application
                // itself now sets — plus no pending milestone reflection.
                return app.ready.log && app.ready.confirm && this.enrollment.isCourseworkComplete && !this.milestoneLock.locked && app.ready.minMinutes && (this.chargeAmount(app) === 0 || (this.chargeAmount(app) > 0 && app.ready.charge));
            }
        },
        mounted() {
            this.getEnrollmentById(this.enrollmentId);
        }
    };
</script>

<style lang="scss" scoped>

</style>
